import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { ethers } from 'ethers';
import { getGameContract, withdrawDividends } from '../utils/contract';

/**
 * Dividends Component - Withdraw Key Holder Earnings
 * 
 * FOMO3D-style dividends from key purchases.
 * "Every key purchase pays the OGs"
 */

interface Titles {
  isWhale: boolean;
  isSniper: boolean;
  isDiamond: boolean;
  isArsonist: boolean;
  isKing: boolean;
}

interface DividendsProps {
  account: string | null;
  onWithdraw?: () => void;
}

const CLAIM_MESSAGES = [
  "Dividends claimed! Time to stack more keys",
  "PAW acquired! Diamond hands rewarded",
  "Cha-ching! Those keys are paying off ★",
  "Based! More PAW for the pot... or not",
];

const TITLE_CONFIG = [
  { 
    key: 'isWhale', 
    icon: '🐳',
    name: 'Whale', 
    description: '1000+ lifetime keys',
    color: 'text-neon-cyan',
    bgColor: 'bg-neon-cyan/10',
    borderColor: 'border-neon-cyan/30'
  },
  { 
    key: 'isSniper', 
    icon: '🎯',
    name: 'Sniper', 
    description: 'Won in last 60 seconds',
    color: 'text-neon-magenta',
    bgColor: 'bg-neon-magenta/10',
    borderColor: 'border-neon-magenta/30'
  },
  { 
    key: 'isDiamond', 
    icon: '💎',
    name: 'Diamond Hands', 
    description: 'Won 5+ rounds',
    color: 'text-neon-purple',
    bgColor: 'bg-neon-purple/10',
    borderColor: 'border-neon-purple/30'
  },
  { 
    key: 'isArsonist', 
    icon: '🔥',
    name: 'Arsonist', 
    description: 'Bought in last 60s',
    color: 'text-nixie-orange',
    bgColor: 'bg-nixie-orange/10',
    borderColor: 'border-nixie-orange/30'
  },
  { 
    key: 'isKing', 
    icon: '👑',
    name: 'King', 
    description: 'Current leader',
    color: 'text-nixie-amber',
    bgColor: 'bg-nixie-amber/10',
    borderColor: 'border-nixie-amber/30'
  },
];

export default function Dividends({ account, onWithdraw }: DividendsProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [pendingDividends, setPendingDividends] = useState<bigint>(0n);
  const [totalKeys, setTotalKeys] = useState<bigint>(0n);
  const [titles, setTitles] = useState<Titles | null>(null);

  // Fetch pending dividends and titles
  const fetchDividends = useCallback(async () => {
    if (!account) {
      setPendingDividends(0n);
      setTotalKeys(0n);
      setTitles(null);
      return;
    }
    
    try {
      const contract = await getGameContract();
      if (contract) {
        const [divs, keys] = await Promise.all([
          contract.withdrawableDividendsOf(account),
          contract.keyBalance(account),
        ]);
        setPendingDividends(divs);
        setTotalKeys(keys);
        
        // Fetch titles
        try {
          const result = await contract.getPlayerTitles(account);
          setTitles({
            isWhale: result.isWhale,
            isSniper: result.isSniper,
            isDiamond: result.isDiamond,
            isArsonist: result.isArsonist,
            isKing: result.isKing,
          });
        } catch (e) {
          console.log('Could not fetch titles');
        }
      }
    } catch (error) {
      console.error('Failed to fetch dividends:', error);
    }
  }, [account]);

  useEffect(() => {
    fetchDividends();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchDividends, 30000);
    return () => clearInterval(interval);
  }, [fetchDividends]);

  const handleWithdraw = async () => {
    if (!account) {
      toast.error("Connect your wallet first!");
      return;
    }

    if (pendingDividends === 0n) {
      toast.error("No dividends to claim yet! Buy more keys");
      return;
    }

    setIsLoading(true);

    try {
      toast.loading('Withdrawing your dividends...', { id: 'withdraw' });

      const success = await withdrawDividends();
      
      if (success) {
        const randomMsg = CLAIM_MESSAGES[Math.floor(Math.random() * CLAIM_MESSAGES.length)];
        toast.success(randomMsg, { 
          id: 'withdraw', 
          duration: 5000,
          style: {
            background: '#0a0a1a',
            color: '#00ff88',
            border: '1px solid rgba(0, 255, 136, 0.3)',
          },
        });
        
        setPendingDividends(0n);
        onWithdraw?.();
      } else {
        throw new Error('Transaction failed');
      }
    } catch (error: any) {
      console.error('Withdraw error:', error);
      
      let errorMsg = 'Failed to withdraw dividends';
      if (error.code === 'ACTION_REJECTED') {
        errorMsg = 'Transaction cancelled';
      } else if (error.message?.includes('No dividends')) {
        errorMsg = 'No dividends available!';
      }
      
      toast.error(errorMsg, { id: 'withdraw' });
    } finally {
      setIsLoading(false);
      fetchDividends();
    }
  };

  const hasDividends = pendingDividends > 0n;
  const formattedDividends = ethers.formatEther(pendingDividends);
  const formattedKeys = Number(totalKeys).toLocaleString();

  return (
    <motion.div
      className="glass-panel-3d relative rounded-3xl overflow-hidden p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ 
        boxShadow: '0 0 60px rgba(0, 255, 255, 0.3), 0 0 120px rgba(153, 50, 204, 0.2)',
      }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-3xl">💰</span>
        <div>
          <h2 className="font-orbitron text-xl text-neon-cyan">Key Dividends</h2>
          <p className="text-xs text-gray-500 font-rajdhani">Earnings from key purchases</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-void-black/50 rounded-lg p-3 border border-neon-magenta/20">
          <p className="text-xs text-gray-500 mb-1 font-rajdhani">Your Keys</p>
          <p className="text-lg font-orbitron text-neon-magenta">🔑 {formattedKeys}</p>
        </div>
        <div className="bg-void-black/50 rounded-lg p-3 border border-neon-cyan/20">
          <p className="text-xs text-gray-500 mb-1 font-rajdhani">Pending PAW</p>
          <p className="text-lg font-orbitron text-neon-cyan">
            {Number(formattedDividends).toLocaleString(undefined, { maximumFractionDigits: 2 })} PAW
          </p>
        </div>
      </div>

      {/* Info */}
      <div className="text-xs text-gray-400 mb-4 p-2 bg-void-black/30 rounded-lg font-rajdhani border border-neon-purple/20">
        <p>🔑 35% of every key purchase is distributed to existing key holders.</p>
        <p className="mt-1">🏆 35% goes to key holders when rounds end.</p>
        <p className="mt-1">🎰 5% builds the jackpot pool for sniper bonuses!</p>
      </div>
      
      {/* Earned Titles Section */}
      {account && titles && (
        <div className="mb-4">
          {(() => {
            const earnedTitles = TITLE_CONFIG.filter(t => titles[t.key as keyof Titles]);
            if (earnedTitles.length === 0) return null;
            
            return (
              <>
                <p className="text-xs text-gray-500 mb-2 font-rajdhani">🏅 Your Earned Titles</p>
                <div className="flex flex-wrap gap-2">
                  {earnedTitles.map((title) => (
                    <motion.div
                      key={title.key}
                      className={`px-3 py-1.5 rounded-lg ${title.bgColor} border ${title.borderColor} flex items-center gap-2`}
                      whileHover={{ scale: 1.05 }}
                      title={title.description}
                    >
                      <span className="text-lg">{title.icon}</span>
                      <span className={`text-xs font-bold font-rajdhani ${title.color}`}>{title.name}</span>
                    </motion.div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}
      
      {/* Withdraw Button */}
      <motion.button
        onClick={handleWithdraw}
        disabled={!hasDividends || isLoading || !account}
        className={`
          w-full py-4 rounded-xl font-orbitron text-lg
          transition-all duration-300
          ${hasDividends && account
            ? 'btn-buy'
            : 'bg-gray-700/50 text-gray-500 cursor-not-allowed border border-gray-600/30'
          }
        `}
        whileHover={hasDividends && account ? { scale: 1.02 } : {}}
        whileTap={hasDividends && account ? { scale: 0.98 } : {}}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <span className="animate-spin">⏳</span>
            Withdrawing...
          </span>
        ) : !account ? (
          <span className="flex items-center justify-center gap-2">
            🔌 Connect Wallet
          </span>
        ) : hasDividends ? (
          <span className="flex items-center justify-center gap-2">
            💰 Withdraw {Number(formattedDividends).toLocaleString(undefined, { maximumFractionDigits: 0 })} PAW
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            🔑 No Dividends Yet
          </span>
        )}
      </motion.button>
    </motion.div>
  );
}

